/**
 * Widoki list dla asystenta handlowca:
 *  - ClientsListView (sales-crm/clients, sales-crm/clients/dictionary, panel/clients/clients)
 *  - OffersListView (sales-crm/my-offers, sales-crm/offers, sales-crm/trader/offers/semantic-search)
 *  - TasksListView (sales-crm/tasks)
 *  - DashboardSummaryView (sales-crm/dashboard, trader/insights/dashboard)
 *
 * Strukturze tool result API zaufamy luzno — kazde pole jest opcjonalne i ma fallback.
 * Klient/oferta/zadanie wzorzec: list z lista `items[]` LUB `response.items[]`.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Person, FileEarmarkText, CheckCircle, GraphUp } from 'react-bootstrap-icons';

import styles from 'theme/components/containers/ChatBase/WiseAi/TraderListsView.module.scss';

const MAX_VISIBLE = 4;

const formatMoney = (value: number | string | undefined, currency = 'PLN'): string => {
  if (value === undefined || value === null || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (Number.isNaN(num)) return '—';
  return `${num.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

const formatDate = (value: string | undefined): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('pl-PL');
  } catch {
    return value;
  }
};

/**
 * Wyciaga liste `items` z payloadu o niepewnym ksztalcie.
 * Backend zwraca rozne ksztalty zaleznie od endpointu (czasem `response.items`,
 * czasem top-level `items`, a w `dictionary` to plaska lista).
 */
const extractItems = (payload: unknown): { items: Array<Record<string, unknown>>; totalCount: number } => {
  if (!payload || typeof payload !== 'object') return { items: [], totalCount: 0 };
  const p = payload as Record<string, unknown>;
  // Format PHP-native ToolSet: { success: true, data: { items, count } }
  // Format REST API: { response: { items, total_count } } lub bezposrednio { items, total_count }
  // Format dictionary: plaska tablica.
  let root = (p.response ?? p) as Record<string, unknown>;
  // Gdy mamy {success, data:{...}} - data jest faktycznym payloadem.
  if (
    typeof p.success === 'boolean'
    && p.data !== undefined
    && p.data !== null
    && typeof p.data === 'object'
    && !Array.isArray(p.data)
  ) {
    root = p.data as Record<string, unknown>;
  }
  if (Array.isArray(root)) {
    return { items: root as Array<Record<string, unknown>>, totalCount: root.length };
  }
  const itemsRaw = root.items ?? root.records ?? (Array.isArray(root.data) ? root.data : null);
  const items = (itemsRaw ?? []) as Array<Record<string, unknown>>;
  const totalCount = (root.total_count ?? root.totalCount ?? root.count ?? items.length) as number;
  return { items: Array.isArray(items) ? items : [], totalCount };
};

const pickFirst = (item: Record<string, unknown>, keys: string[]): string | undefined => {
  for (const k of keys) {
    const v = item[k];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return undefined;
};

interface ListViewProps {
  payload: unknown;
}

// === Clients ===
export const ClientsListView: React.FC<ListViewProps> = ({ payload }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { items, totalCount } = extractItems(payload);

  if (items.length === 0) {
    return (
      <div className={styles.empty}>
        <Person size={20} />
        <span>{t('Nie znaleziono klientów spełniających kryteria.')}</span>
      </div>
    );
  }

  const visible = showAll ? items : items.slice(0, MAX_VISIBLE);
  const remaining = items.length - visible.length;

  return (
    <div className={styles.wrap}>
      <div className={styles.headline}>
        {t('Znaleziono')} <strong>{totalCount}</strong> {t('klientów')}
      </div>
      <div className={styles.list}>
        {visible.map((item, idx) => {
          const id = pickFirst(item, ['id', 'client_id', 'clientId']) ?? String(idx);
          const name = pickFirst(item, ['name', 'company_name', 'full_name', 'client_name']);
          const nip = pickFirst(item, ['nip', 'tax_id', 'vat_id']);
          const email = pickFirst(item, ['email', 'contact_email']);
          const totalRevenue = item.total_revenue ?? item.totalRevenue;
          const lastOrder = pickFirst(item, ['last_order_date', 'lastOrderDate']);

          return (
            <div key={id} className={styles.card}>
              <div className={styles.icon}><Person size={22} /></div>
              <div className={styles.body}>
                <div className={styles.title}>{name ?? `#${id}`}</div>
                {(nip || email) && (
                  <div className={styles.meta}>
                    {nip && <span><strong>NIP:</strong> {nip}</span>}
                    {email && <span><strong>Email:</strong> {email}</span>}
                  </div>
                )}
                {(totalRevenue !== undefined || lastOrder) && (
                  <div className={styles.meta}>
                    {totalRevenue !== undefined && (
                      <span><strong>{t('Obroty')}:</strong> {formatMoney(totalRevenue as number)}</span>
                    )}
                    {lastOrder && (
                      <span><strong>{t('Ostatnie zamówienie')}:</strong> {formatDate(lastOrder)}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {remaining > 0 && (
        <button type="button" className={styles.moreBtn} onClick={() => setShowAll(true)}>
          {t('Pokaż pozostałe')} ({remaining})
        </button>
      )}
    </div>
  );
};

// === Offers ===
export const OffersListView: React.FC<ListViewProps> = ({ payload }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { items, totalCount } = extractItems(payload);

  if (items.length === 0) {
    return (
      <div className={styles.empty}>
        <FileEarmarkText size={20} />
        <span>{t('Nie znaleziono ofert spełniających kryteria.')}</span>
      </div>
    );
  }

  const visible = showAll ? items : items.slice(0, MAX_VISIBLE);
  const remaining = items.length - visible.length;

  return (
    <div className={styles.wrap}>
      <div className={styles.headline}>
        {t('Znaleziono')} <strong>{totalCount}</strong> {t('ofert')}
      </div>
      <div className={styles.list}>
        {visible.map((item, idx) => {
          const id = pickFirst(item, ['id', 'offer_id']) ?? String(idx);
          const name = pickFirst(item, ['name', 'title', 'offer_name']);
          const clientName = pickFirst(item, ['client_name', 'clientName']);
          const status = pickFirst(item, ['status', 'state']);
          const totalNet = item.total_net ?? item.totalNet ?? item.value_net;
          const validUntil = pickFirst(item, ['valid_until', 'validUntil', 'expiry_date']);
          const positionCount = item.position_count ?? item.positions_count ?? item.positionsCount;

          return (
            <div key={id} className={styles.card}>
              <div className={styles.icon}><FileEarmarkText size={22} /></div>
              <div className={styles.body}>
                <div className={styles.title}>{name ?? `#${id}`}</div>
                <div className={styles.meta}>
                  {clientName && <span><strong>{t('Klient')}:</strong> {clientName}</span>}
                  {status && <span className={styles.status} data-status={status.toLowerCase()}>{status}</span>}
                </div>
                <div className={styles.meta}>
                  {totalNet !== undefined && (
                    <span><strong>{t('Wartość netto')}:</strong> {formatMoney(totalNet as number)}</span>
                  )}
                  {positionCount !== undefined && (
                    <span><strong>{t('Pozycji')}:</strong> {String(positionCount)}</span>
                  )}
                  {validUntil && (
                    <span><strong>{t('Ważna do')}:</strong> {formatDate(validUntil)}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {remaining > 0 && (
        <button type="button" className={styles.moreBtn} onClick={() => setShowAll(true)}>
          {t('Pokaż pozostałe')} ({remaining})
        </button>
      )}
    </div>
  );
};

// === Tasks ===
export const TasksListView: React.FC<ListViewProps> = ({ payload }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { items, totalCount } = extractItems(payload);

  if (items.length === 0) {
    return (
      <div className={styles.empty}>
        <CheckCircle size={20} />
        <span>{t('Brak zadań do wyświetlenia.')}</span>
      </div>
    );
  }

  const visible = showAll ? items : items.slice(0, MAX_VISIBLE);
  const remaining = items.length - visible.length;

  return (
    <div className={styles.wrap}>
      <div className={styles.headline}>
        {t('Znaleziono')} <strong>{totalCount}</strong> {t('zadań')}
      </div>
      <div className={styles.list}>
        {visible.map((item, idx) => {
          const id = pickFirst(item, ['id', 'task_id']) ?? String(idx);
          const title = pickFirst(item, ['title', 'name', 'subject']);
          const clientName = pickFirst(item, ['client_name', 'clientName']);
          const dueDate = pickFirst(item, ['due_date', 'dueDate', 'deadline']);
          const description = pickFirst(item, ['description', 'notes']);

          return (
            <div key={id} className={styles.card}>
              <div className={styles.icon}><CheckCircle size={22} /></div>
              <div className={styles.body}>
                <div className={styles.title}>{title ?? `#${id}`}</div>
                {(clientName || dueDate) && (
                  <div className={styles.meta}>
                    {clientName && <span><strong>{t('Klient')}:</strong> {clientName}</span>}
                    {dueDate && <span><strong>{t('Termin')}:</strong> {formatDate(dueDate)}</span>}
                  </div>
                )}
                {description && <div className={styles.description}>{description}</div>}
              </div>
            </div>
          );
        })}
      </div>
      {remaining > 0 && (
        <button type="button" className={styles.moreBtn} onClick={() => setShowAll(true)}>
          {t('Pokaż pozostałe')} ({remaining})
        </button>
      )}
    </div>
  );
};

// === Dashboard summary ===
export const DashboardSummaryView: React.FC<ListViewProps> = ({ payload }) => {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const response = (p.response ?? p) as Record<string, unknown>;
  const entries = Object.entries(response).filter(([, v]) => {
    return typeof v === 'number' || typeof v === 'string';
  });
  if (entries.length === 0) return null;

  return (
    <div className={styles.wrap}>
      <div className={styles.dashboardGrid}>
        {entries.slice(0, 8).map(([key, value]) => (
          <div key={key} className={styles.dashboardTile}>
            <div className={styles.dashboardIcon}><GraphUp size={18} /></div>
            <div className={styles.dashboardLabel}>{key.replace(/_/g, ' ')}</div>
            <div className={styles.dashboardValue}>
              {typeof value === 'number' ? value.toLocaleString('pl-PL') : String(value)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
